import React, { useState, useRef, useEffect } from "react";
import {
  List,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  Paper,
  ThemeProvider,
  createTheme,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StorageIcon from "@mui/icons-material/Storage";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ApprovalIcon from '@mui/icons-material/Approval';
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import "../SideDrawer/SideDrawer.css";
import {
  controlTowerMenuItems,
  appRootUrl,
} from "../../../routes/PrivateRoutes";

const drawerWidth = 56;
const submenuWidth = 175;
const itemHeight = 40;
const seaGreen = "#6AC9CE";

const topCategories = {
  Supply: ["Inbound Shipments", "Inbound Deliveries", "Purchase Orders"],
  Demand: ["Outbound Shipments", "Outbound Deliveries", "Sales Orders"],
  Inventory: ["Inventory", "Warehouse Capacity"],
};

const bottomCategories = {
  "Master Data": [
    "Currency Conversion",
    "Fiscal Calendar",
    "Documents",
    "Items",
    "Sites",
    "Nodes",
    "Sourcing Lanes",
    "Participants",
    "Batches",
    "BODs",
    "BOMs",
  ],
  "Data Hub": ["Traceability", "Data Quality"],
};

const staticIcons = {
  Home: <HomeIcon />,
  "New Request": <NoteAddIcon />,
  "Request Status": <TrackChangesIcon />,
  "Report": <AssessmentIcon />,
  "Approval Delegation": <ApprovalIcon />,
  Dashboard: <DashboardIcon />,
  Supply: <LocalShippingIcon />,
  Demand: <LocalMallIcon />,
  Inventory: <Inventory2Icon />,
  "Master Data": <StorageIcon />,
  "Data Hub": <DataUsageIcon />,
  Analytics: <AssessmentIcon />,
  Admin: <SupervisorAccountIcon />,
};

const SideDrawer = ({ contentHeight }) => {
  const ctAppRoles = useSelector((state) => state.user.ctAppRoles);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [submenuTop, setSubmenuTop] = useState(contentHeight || 100);
  const arrowRefs = useRef({});
  const drawerRef = useRef(null);
  const submenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isUserRolesPage = location.pathname.startsWith("/core/user-roles/");
  const isProd = process.env.REACT_APP_ENVT === 'PROD';
  const isAdminUser = Array.isArray(ctAppRoles) && ctAppRoles.includes("CT-ADM-USER");

  const disabledUserRolesMenus = [
    ...(isAdminUser ? [] : ["Report"]),
    //...(isProd ? ["Approval Delegation"] : [])
  ];

  const theme = createTheme({
    typography: {
      fontSize: 11,
    },
  });

  const labelToPathMap = Object.fromEntries(
    controlTowerMenuItems.map(({ label, path }) => [label, path])
  );
  // const userRolesLabelToPathMap = {
  //   "New Request": "/core/user-roles/new-request",
  //   "Request Status": "/core/user-roles/track-access-request",
  //   "Report": "/core/user-roles/reports",
  //   "Approval Delegation": "/core/user-roles/approval-rights",
  // };

  const subOptionToCategoryMap = Object.entries({
    ...topCategories,
    ...bottomCategories,
  }).reduce((acc, [category, options]) => {
    options.forEach((option) => {
      acc[option] = category;
    });
    return acc;
  }, {});

  useEffect(() => {
    const currentPath = location.pathname;
    // const matched = Object.entries(labelToPathMap).find(
    //   ([, path]) =>
    //     currentPath === path || currentPath.startsWith(path + "/")
    // );
    // if (matched) {
    //   setSelectedLabel(matched[0]);
    //   setSelectedCategory(null);
    //   return;
    // }

    // Command Center logic
    const matchedEntry = Object.entries(labelToPathMap).find(([, path]) =>
      currentPath.startsWith(path)
    );
    if (matchedEntry) {
      const matchedLabel = matchedEntry[0];
      setSelectedLabel(matchedLabel);
      const categoryFromLabel = subOptionToCategoryMap[matchedLabel];
      setSelectedCategory(categoryFromLabel || null);
    }
  }, [location.pathname, isUserRolesPage]);

  const handleRedirect = (label, menu) => {
    if (!isUserRolesPage) {
      setSelectedLabel(label);
      setSelectedCategory(menu || null);
    }
    const path = labelToPathMap[label];
    if (label === "Home") {
      window.location.href = appRootUrl;
      return;
    }
    navigate(path || "/blank");
    setShowSubmenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        submenuRef.current &&
        !submenuRef.current.contains(event.target) &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setShowSubmenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryClick = (category) => {
    const rect = arrowRefs.current[category]?.getBoundingClientRect();
    const offsetTop = rect?.top || 0;
    const drawerTop = contentHeight || 0;
    const estimatedHeight = (allCategories[category]?.length || 0) * itemHeight;
    const availableSpaceBelow = window.innerHeight - offsetTop;
    const availableSpaceAbove = offsetTop - drawerTop;
    let adjustedTop = offsetTop - drawerTop;

    if (
      estimatedHeight > availableSpaceBelow &&
      availableSpaceAbove > availableSpaceBelow
    ) {
      adjustedTop = Math.max(
        0,
        offsetTop - drawerTop - estimatedHeight + itemHeight
      );
    } else if (adjustedTop + estimatedHeight > window.innerHeight - drawerTop) {
      adjustedTop = Math.max(
        0,
        window.innerHeight - drawerTop - estimatedHeight
      );
    }
    setSubmenuTop(adjustedTop);

    if (selectedCategory === category && showSubmenu) {
      setShowSubmenu(false);
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
      setShowSubmenu(true);
    }
  };
  console.log('env.....', process.env.REACT_APP_ENVT)
  let enabledSubmenus;
  const allCategories = { ...topCategories, ...bottomCategories };
  const subOptions = selectedCategory ? allCategories[selectedCategory] : [];
  const hasInventoryAccess = Array.isArray(ctAppRoles) && ctAppRoles.includes("CT-INVENTORY");
  //const hasInventoryAccess = true;
  if (!hasInventoryAccess) {
    enabledSubmenus = {
      Supply: ["Inbound Shipments", "Inbound Deliveries", "Purchase Orders"],
      Demand: ["Outbound Shipments", "Outbound Deliveries", "Sales Orders"],
      "Master Data": ["Currency Conversion"]
    }
  } else {
    enabledSubmenus = {
      Supply: ["Inbound Shipments", "Inbound Deliveries", "Purchase Orders"],
      Demand: ["Outbound Shipments", "Outbound Deliveries", "Sales Orders"],
      Inventory: ["Inventory"],
      "Master Data": ["Currency Conversion"]
    }
  }

  const renderNavButton = (
    label,
    isActive,
    onClick,
    ref = null,
    disabled = false
  ) => (
    <ListItemButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        backgroundColor: isActive ? seaGreen : "transparent",
        transition: "background-color 0.2s",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
        "&:hover": {
          backgroundColor: disabled ? "transparent" : seaGreen,
          "& .nav-icon, & .nav-label": {
            color: disabled ? "#000" : "#fff",
          },
        },
      }}
    >
      <Box
        className="nav-icon"
        sx={{
          color: isActive ? "#fff" : "#000",
          mb: 0.3,
          "& svg": { fontSize: "1.1rem" },
        }}
      >
        {staticIcons[label]}
      </Box>
      <Typography
        className="nav-label"
        variant="caption"
        sx={{
          fontSize: "0.50rem",
          color: isActive ? "#fff" : "#000",
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
    </ListItemButton>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        ref={drawerRef}
        sx={{
          width: drawerWidth,
          backgroundColor: isUserRolesPage ? "#bcbcbc" : "#ddd",
          color: "#fff",
          position: "fixed",
          top: contentHeight || 0,
          bottom: 20,
          left: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 0,
          pb: isUserRolesPage ? 20 : 0,
          height: `calc(100vh - ${contentHeight || 0})px`,
        }}
      >
        {isUserRolesPage ? (
          <>
            {[
              "Home",
              "New Request",
              "Request Status",
              "Report",
              "Approval Delegation",
            ].map(label =>
              renderNavButton(
                label,
                selectedLabel === label,
                () => handleRedirect(label, label),
                null,
                disabledUserRolesMenus.includes(label)
              )
            )}
          </>
        ) : (
          <>
            {["Home", "Dashboard"].map((label) =>
              renderNavButton(
                label,
                selectedLabel === label,
                () => handleRedirect(label, label)
              )
            )}

            <Divider sx={{ width: "100%", borderColor: "#888" }} />

            {Object.keys(topCategories).map((category) =>
              renderNavButton(
                category,
                selectedCategory === category,
                () => handleCategoryClick(category),
                (el) => (arrowRefs.current[category] = el)
              )
            )}

            {Object.keys(bottomCategories).map((category) =>
              renderNavButton(
                category,
                selectedCategory === category,
                () => handleCategoryClick(category),
                (el) => (arrowRefs.current[category] = el)
              )
            )}

            <Divider sx={{ width: "100%", borderColor: "#888", mt: 0 }} />

            {["Analytics", "Admin"].map((label) =>
              renderNavButton(
                label,
                selectedLabel === label,
                () => handleRedirect(label, label)
              )
            )}
          </>
        )}
      </Box>

      {showSubmenu && selectedCategory && (
        <Paper
          elevation={4}
          className="submenu-panel"
          ref={submenuRef}
          sx={{
            position: "fixed",
            top: submenuTop + (contentHeight || 0),
            left: drawerWidth,
            width: submenuWidth,
            padding: 0.5,
            boxShadow: "none",
          }}
        >
          <List disablePadding>
            {subOptions.map((label) => {
              const isEnabled =
                enabledSubmenus[selectedCategory]?.includes(label) || false;

              return (
                <ListItemButton
                  key={label}
                  className="submenu-item"
                  disabled={!isEnabled}
                  onClick={() =>
                    isEnabled && handleRedirect(label, selectedCategory)
                  }
                  sx={{
                    opacity: isEnabled ? 1 : 0.5,
                    cursor: isEnabled ? "pointer" : "not-allowed",
                  }}
                >
                  <ListItemText primary={label} />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </ThemeProvider>
  );
};

export default SideDrawer;